import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { History, Loader2 } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { OrgTabs } from "@/components/OrgTabs";
import { Button } from "@/components/ui/button";
import { useOrg } from "@/hooks/useOrg";
import { api } from "@/lib/api";

export default function OrgAudit() {
  const { slug } = useParams();
  const { org } = useOrg(slug);
  const [rows, setRows] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const isAdmin = org && (org.role === "owner" || org.role === "admin");

  const load = useCallback(async (before) => {
    if (!slug) return [];
    const { data } = await api.get(`/orgs/${slug}/audit`, { params: before ? { before, limit: 50 } : { limit: 50 } });
    return data;
  }, [slug]);

  useEffect(() => {
    load().then(setRows).catch(() => setRows([]));
  }, [load]);

  const loadMore = async () => {
    if (!rows?.length) return;
    setLoadingMore(true);
    try {
      const more = await load(rows[rows.length - 1].created_at);
      setRows((r) => [...r, ...more]);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <AppShell>
      <OrgTabs slug={slug} />
      <div className="flex items-center gap-2">
        <History className="h-5 w-5 text-[color:var(--rb-accent)]" />
        <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">
          Audit log{org ? ` — ${org.name}` : ""}
        </h1>
      </div>
      <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">Every membership, key, and version action, append-only.</p>

      {org && !isAdmin ? (
        <div className="mt-8 text-center text-sm text-[color:var(--rb-text-faint)]">Admin access required.</div>
      ) : rows === null ? (
        <div className="mt-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[color:var(--rb-text-faint)]" /></div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-2xl border border-[color:var(--rb-border)]">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--rb-code)] text-xs text-[color:var(--rb-text-faint)]">
              <tr>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">Action</th>
                <th className="px-4 py-2 text-left">Target</th>
                <th className="px-4 py-2 text-left">Actor</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid="audit-row" className="border-t border-[color:var(--rb-border)]">
                  <td className="px-4 py-2 text-[color:var(--rb-text-faint)]">{(r.created_at || "").replace("T", " ").slice(0, 19)}</td>
                  <td className="rb-mono px-4 py-2 text-[color:var(--rb-text)]">{r.action}</td>
                  <td className="rb-mono px-4 py-2 text-[color:var(--rb-text-dim)]">{r.target || "—"}</td>
                  <td className="px-4 py-2 text-[color:var(--rb-text-dim)]">{r.actor_id}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-xs text-[color:var(--rb-text-faint)]">No audit events yet.</td></tr>
              )}
            </tbody>
          </table>
          {rows.length >= 50 && (
            <div className="border-t border-[color:var(--rb-border)] p-3 text-center">
              <Button variant="ghost" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="audit-load-more">
                {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load more"}
              </Button>
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
